/** Formátovací a prezentační pomocníci (ceny, štítky, ikony, barvy křídy). */

import {
  Atom,
  BookOpenCheck,
  Calculator,
  FlaskConical,
  Globe,
  GraduationCap,
  Landmark,
  Languages,
  LayoutGrid,
  Leaf,
  PencilRuler,
  PlayCircle,
  ScrollText,
  Target,
  type LucideIcon,
} from 'lucide-react';
import type { ChalkColor, ContentType, ProductCard, SubjectRef } from './api';

const NBSP = '\u00A0';

/** Cena „89 Kč" s nezlomitelnou mezerou; 0 Kč → „Zdarma". */
export function formatPrice(czk: number): string {
  if (czk <= 0) return 'Zdarma';
  return `${czk}${NBSP}Kč`;
}

export function isFree(czk: number): boolean {
  return czk <= 0;
}

/**
 * Český plurál pro počítadlo výsledků (proposal 003 §4):
 * 1 materiál · 2 až 4 materiály · 0 a 5+ materiálů. Číslo je vždy `tabular-nums`.
 */
export function pluralMaterial(count: number): string {
  const word = count === 1 ? 'materiál' : count >= 2 && count <= 4 ? 'materiály' : 'materiálů';
  return `${count} ${word}`;
}

/** Ročník je smysluplný, jen když nese znak. Prázdný nebo pouhá pomlčka se ignoruje. */
function meaningfulGrade(grade: string | null | undefined): string | null {
  const g = grade?.trim();
  if (!g) return null;
  // Jen pomlčky/tečky = žádný smysluplný ročník. Kódy: hyphen, en, em, tečka, middot.
  const DASHY = new Set([45, 8211, 8212, 46, 183]);
  if ([...g].every((ch) => DASHY.has(ch.charCodeAt(0)))) return null;
  return g;
}

/** Délka videa v sekundách → „1 h 20 min" / „45 min". */
export function formatDuration(seconds: number): string {
  const total = Math.round(seconds / 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h > 0) return m > 0 ? `${h}${NBSP}h ${m}${NBSP}min` : `${h}${NBSP}h`;
  return `${m}${NBSP}min`;
}

/** Délka lekce „2:37" (mm:ss). */
export function formatClock(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function contentTypeLabel(type: ContentType): string {
  switch (type) {
    case 'VIDEO':
      return 'Videokurz';
    case 'APP':
      return 'Aplikace';
    case 'PDF':
    default:
      return 'PDF';
  }
}

/** České skloňování slova „strana": 1 strana, 2 až 4 strany, jinak stran. */
function pluralStrany(n: number): string {
  if (n === 1) return `${n} strana`;
  if (n >= 2 && n <= 4) return `${n} strany`;
  return `${n} stran`;
}

/** Typový štítek: „PDF · 24 stran", „Videokurz · 1 h 20 min", „Aplikace · licenční klíč". */
export function typeBadge(
  p: Pick<ProductCard, 'content_type' | 'page_count' | 'duration_s'>,
): string {
  const label = contentTypeLabel(p.content_type);
  if (p.content_type === 'VIDEO' && p.duration_s) {
    return `${label} · ${formatDuration(p.duration_s)}`;
  }
  if (p.content_type === 'PDF' && p.page_count) {
    return `${label} · ${pluralStrany(p.page_count)}`;
  }
  if (p.content_type === 'APP') {
    return `${label} · licenční klíč`;
  }
  return label;
}

/** Neutrální náhrada názvu předmětu, když v datech chybí/je poškozený. */
const SUBJECT_FALLBACK = 'Materiál';

/** Název předmětu (`subject` je objekt z číselníku); chybí-li, neutrální fallback. */
export function subjectLabel(subject: SubjectRef | null | undefined): string {
  return subject?.name?.trim() || SUBJECT_FALLBACK;
}

/** Sekundární řádek karty „Matematika · 9. třída"; předmět je defenzivně volitelný. */
export function subjectGrade(p: { subject?: SubjectRef | null; grade: string | null }): string {
  const name = subjectLabel(p.subject);
  const grade = meaningfulGrade(p.grade);
  return grade ? `${name} · ${grade}` : name;
}

/* Ikony (sdílený číselník pro kategorie i předměty, proposal 002 §4.4) */

/** Kód ikony → lucide komponenta; neznámý kód → `graduation-cap`. */
export function iconForCode(code: string): LucideIcon {
  switch (code) {
    case 'math':
      return Calculator;
    case 'physics':
      return Atom;
    case 'chem':
      return FlaskConical;
    case 'bio':
      return Leaf;
    case 'lang':
      return Languages;
    case 'geo':
      return Globe;
    case 'hist':
      return Landmark;
    case 'exam':
      return Target;
    case 'cheat':
      return ScrollText;
    case 'sheet':
      return BookOpenCheck;
    case 'video':
      return PlayCircle;
    case 'pencil':
      return PencilRuler;
    case 'prehled':
      return LayoutGrid;
    case 'grad':
    default:
      return GraduationCap;
  }
}

/** Ikona kategorie dle jejího `icon` kódu. */
export const categoryIcon = iconForCode;

/** Ikona předmětu dle jeho `icon` kódu. */
export const subjectIcon = iconForCode;

/** Pořadí a popisky ikon pro pickery v adminu. */
export const ICON_OPTIONS: { code: string; label: string }[] = [
  { code: 'math', label: 'Matematika' },
  { code: 'physics', label: 'Fyzika' },
  { code: 'chem', label: 'Chemie' },
  { code: 'bio', label: 'Biologie' },
  { code: 'lang', label: 'Jazyky' },
  { code: 'geo', label: 'Zeměpis' },
  { code: 'hist', label: 'Dějepis' },
  { code: 'exam', label: 'Přijímačky' },
  { code: 'cheat', label: 'Taháky' },
  { code: 'sheet', label: 'Pracovní listy' },
  { code: 'video', label: 'Videokurzy' },
  { code: 'pencil', label: 'Výtvarka' },
  { code: 'prehled', label: 'Přehledy' },
  { code: 'grad', label: 'Obecné' },
];

/* Barvy křídy (proposal 002 §5.2) */

/** HEX barvy křídy pro SVG glyphy na náhledové ploše. */
export function chalkHex(color: ChalkColor): string {
  switch (color) {
    case 'yellow':
      return '#F6C445';
    case 'blue':
      return '#9BC4E2';
    case 'pink':
      return '#E8A0A8';
    case 'green':
      return '#B5D99C';
    case 'white':
    default:
      return '#F3EFE4';
  }
}

/** Tailwind třída pozadí pro barevnou tečku / swatch křídy. */
export const CHALK_SWATCH: Record<ChalkColor, string> = {
  white: 'bg-chalk',
  yellow: 'bg-accent',
  blue: 'bg-chalkblue',
  pink: 'bg-chalkpink',
  green: 'bg-chalkgreen',
};

/** Pořadí a popisky barev křídy pro pickery v adminu. */
export const CHALK_OPTIONS: { code: ChalkColor; label: string }[] = [
  { code: 'white', label: 'Bílá' },
  { code: 'yellow', label: 'Žlutá' },
  { code: 'blue', label: 'Modrá' },
  { code: 'pink', label: 'Růžová' },
  { code: 'green', label: 'Zelená' },
];
